/*import useJwt from '@/auth/jwt/useJwt'

// axios
import axios from 'axios'

const axiosIns = axios.create({
  // You can add your headers here
  // ================================
  // baseURL: 'https://some-domain.com/api/',
  // timeout: 1000,
  // headers: {'X-Custom-Header': 'foobar'}
})

//var tokenJWT =  localStorage.getItem('accessToken')
//axiosIns.defaults.headers.common['Authorization'] = "`Bearer ${ tokenJWT }`"

const responseHandler = response => {

    if(response.headers?.authorization){
       const newToken = response.headers.authorization.split(' ')[1]
       useJwt.setToken(newToken)
    }

    return response;
};

const errorHandler = error => {
   const originalRequest = error.config

   if (error.response.status === 401) {
      if(error.config.url.includes("www.migros")){
         if(error.response.data['leshop-error'] == 'expiredtoken'){
            localStorage.removeItem('tokenMigros')
            //return originalRequest
         }
      }

      if(error.response.data && error.response.data.message == 'Not valid payment status!'){
         window.location.href = error.response.data.url;
      }
      //   localStorage.removeItem('accessToken')
      //   localStorage.removeItem('userData')
    }

    return Promise.reject(error);
};

axiosIns.interceptors.response.use(
    (response) => responseHandler(response),
    (error) => errorHandler(error)
 );

export default axiosIns*/

import axios from 'axios'
import useJwt from '@/auth/jwt/useJwt'
import {
  clearDemoAvailability, demoAvailabilityCode, getDemoAvailabilityBarrierVersion,
  getDemoAvailabilityCode, isLocalApiRequest, isRetryableDemoRead, reportDemoAvailabilityError,
  synchronizeDemoAvailabilitySession,
} from './demoAvailability'

// Création de l'instance Axios
const axiosIns = axios.create({
  // Tu peux ajouter ici des options globales si besoin
  // baseURL: 'https://some-domain.com/api/',
  // timeout: 10000,
  // headers: { 'X-Custom-Header': 'foobar' }
  validateStatus: status => status >= 200 && status < 300,
})

// Keep the shared client compatible with the pinned Axios request validator.
axiosIns.defaults.transitional = {
  silentJSONParsing: true,
  forcedJSONParsing: true,
  clarifyTimeoutError: false,
  legacyInterceptorReqResOrdering: true,
}

axiosIns.interceptors.request.use((config) => {
  const url = config.url || ''
  if (config.demoReadRetryGuard && !config.demoReadRetryGuard()) {
    throw new axios.CanceledError('Demo read retry cancelled.', config)
  }
  synchronizeDemoAvailabilitySession(localStorage.getItem(useJwt.jwtConfig?.storageTokenKeyName || 'accessToken'))

  // Si c'est un appel externe (commence par http) et que l'URL ne contient pas certains mots-clés => on supprime Authorization
  if (
    url.startsWith('http') &&
    !url.includes('myproduct') &&
    !url.includes('mysupp') &&
    !url.includes('ciqual') &&
    !url.includes('usda') &&
    !url.includes('amavita') &&
    !url.includes('sunstore')
  ) {
    delete config.headers.Authorization
  }

  return config
})

const sentBearerToken = config => {
  const authorization = config?.headers?.get?.('Authorization')
    || config?.headers?.Authorization || config?.headers?.authorization
  return typeof authorization === 'string' ? authorization.match(/^Bearer\s+(\S+)$/i)?.[1] : null
}

const waitForDemoReadRetry = (signal, retryAfter) => new Promise((resolve, reject) => {
  let timeout
  const cancel = () => {
    clearTimeout(timeout)
    signal?.removeEventListener('abort', cancel)
    reject(new axios.CanceledError('Demo read retry cancelled.'))
  }
  if (signal?.aborted) { cancel(); return }
  signal?.addEventListener('abort', cancel, { once: true })
  const retryAfterSeconds = Number(retryAfter)
  const delay = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
    ? Math.min(retryAfterSeconds * 1000, 2000) : 250
  timeout = setTimeout(() => {
    signal?.removeEventListener('abort', cancel)
    resolve()
  }, delay + Math.floor(Math.random() * 251))
})

// Gestion des réponses (mise à jour du token JWT si présent)
const responseHandler = (response) => {
  const refreshedAuthorization = response.headers?.get?.('Authorization') || response.headers?.authorization
  const refreshedToken = typeof refreshedAuthorization === 'string'
    ? refreshedAuthorization.match(/^Bearer\s+(\S+)$/i)?.[1] : null
  const sentToken = sentBearerToken(response.config)
  const currentToken = localStorage.getItem(useJwt.jwtConfig?.storageTokenKeyName || 'accessToken')
  // An older response must not replace a token installed by a later login or security change.
  if (refreshedToken && currentToken && sentToken === currentToken) useJwt.setToken(refreshedToken, { refreshedFrom: sentToken })
  return response
}

// Gestion des erreurs
const errorHandler = async (error) => {
  // Vérifie qu'il s'agit bien d'une erreur Axios
  if (axios.isAxiosError(error)) {
    const { response, config } = error
    const currentToken = localStorage.getItem(useJwt.jwtConfig?.storageTokenKeyName || 'accessToken')
    const sentToken = sentBearerToken(config)
    if (currentToken && sentToken === currentToken && isLocalApiRequest(config)
      && getDemoAvailabilityCode(error)) {
      const version = getDemoAvailabilityBarrierVersion()
      if (getDemoAvailabilityCode(error) === 'demo_busy' && isRetryableDemoRead(config)
        && !['demo_reset_in_progress', 'demo_unavailable'].includes(demoAvailabilityCode.value)
        && !config.signal?.aborted) {
        // One short jittered retry spreads the initial burst without replaying a
        // mutation, a costly generator, a reset refusal or an unavailable server.
        const retryAllowed = () => localStorage.getItem(useJwt.jwtConfig?.storageTokenKeyName || 'accessToken') === sentToken
          && version === getDemoAvailabilityBarrierVersion() && !config.signal?.aborted
        await waitForDemoReadRetry(config.signal,
          response.headers?.get?.('Retry-After') || response.headers?.['retry-after'])
        if (retryAllowed()) {
          return axiosIns.request({ ...config, demoReadRetried: true, demoReadRetryGuard: retryAllowed })
        }
      }
      if (localStorage.getItem(useJwt.jwtConfig?.storageTokenKeyName || 'accessToken') === sentToken
        && version === getDemoAvailabilityBarrierVersion()) {
        reportDemoAvailabilityError(error, sentToken)
      }
    }

    // Gestion des erreurs 401
    if (response?.status === 401) {
      if (currentToken && sentBearerToken(config) === currentToken
        && ['unauthenticated', 'followup_goal_unauthenticated'].includes(response.data?.code)) {
        clearDemoAvailability()
        localStorage.removeItem(useJwt.jwtConfig?.storageTokenKeyName || 'accessToken')
        localStorage.removeItem('userData')
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('yournutri:auth-session-expired'))
      }
      // Cas spécifique Migros token expiré
      if (config?.url?.includes('www.migros')) {
        if (response.data['leshop-error'] === 'expiredtoken') {
          localStorage.removeItem('tokenMigros')
        }
      }

      // Cas spécifique paiement invalide
      if (response.data && response.data.message === 'Not valid payment status!') {
        window.location.href = response.data.url
      }

      // Ici tu peux gérer un logout global si nécessaire
      // localStorage.removeItem('accessToken')
      // localStorage.removeItem('userData')
    }
  }

  // Toujours rejeter l'erreur pour que l'appelant puisse la gérer
  return Promise.reject(error)
}

// Application des interceptors
axiosIns.interceptors.response.use(
  (response) => responseHandler(response),
  (error) => errorHandler(error)
)

export default axiosIns
